/**
 * PlatformHealthTriageWidget — super-admin home page widget that
 * aggregates the three platform-health signals (Code Health · Data
 * Health · Unusual Sign-In Locations) into a single triage panel.
 *
 * Self-hides when every signal is `ok` so daily-clean platforms
 * never see it. When something needs attention, each row shows
 * the count, summary, contextual detail, and an "Open" button
 * that links to the source page (Code Health / Data Health /
 * Admin Dashboard) for deeper inspection + one-click remediation.
 *
 * Polls every 90s with `visibilitychange`-aware pausing so it
 * never hammers the API in backgrounded tabs.
 *
 * Mirrors the design vocabulary of the page-level "right now"
 * strips (rose for code, amber for data, slate for orphan refs)
 * but unifies them under a single super-admin home callout.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  ShieldAlert, Activity, Database, Globe, ChevronRight, RefreshCw, CheckCircle2, AlertTriangle,
} from 'lucide-react';
import api from '../../lib/api';

const POLL_INTERVAL_MS = 90_000;

const KIND_ICON = {
  code_health: Activity,
  data_health: Database,
  unusual_logins: Globe,
};

const SEVERITY_TONE = {
  ok: {
    ring: 'ring-emerald-200',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    icon: 'text-emerald-600',
    badge: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    button: 'border-emerald-400 text-emerald-700 hover:bg-emerald-50',
  },
  warn: {
    ring: 'ring-amber-200',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    icon: 'text-amber-600',
    badge: 'bg-amber-100 text-amber-800 border-amber-300',
    button: 'border-amber-400 text-amber-800 hover:bg-amber-100',
  },
  critical: {
    ring: 'ring-rose-200',
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    icon: 'text-rose-600',
    badge: 'bg-rose-100 text-rose-800 border-rose-300',
    button: 'border-rose-400 text-rose-700 hover:bg-rose-50',
  },
};

const PlatformHealthTriageWidget = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const intervalRef = useRef(null);

  const fetchTriage = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await api.get('/admin/platform-health/triage');
      setData(res?.data || null);
    } catch {
      // Silent on transient blips — telemetry, not a critical path.
    } finally {
      setLoaded(true);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchTriage();
    const onVisibility = () => {
      if (!document.hidden) fetchTriage();
    };
    document.addEventListener('visibilitychange', onVisibility);
    intervalRef.current = setInterval(() => {
      if (!document.hidden) fetchTriage();
    }, POLL_INTERVAL_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchTriage]);

  // Self-hide when there's nothing to triage. Daily-clean platforms
  // never see this widget, which is the win condition.
  if (!loaded || !data || data.open_signal_count === 0) return null;

  const overallTone = SEVERITY_TONE[data.overall_severity] || SEVERITY_TONE.warn;

  return (
    <Card
      className={`mb-6 ${overallTone.border} ${overallTone.bg}/60 ring-1 ${overallTone.ring}`}
      data-testid="platform-health-triage"
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldAlert className={`h-4 w-4 ${overallTone.icon}`} />
            <span className="text-slate-900">Platform Health Triage</span>
            <Badge
              className={overallTone.badge}
              data-testid="platform-health-open-count"
            >
              {data.open_signal_count} signal{data.open_signal_count === 1 ? '' : 's'} open
            </Badge>
            {refreshing && (
              <RefreshCw className="h-3 w-3 text-slate-400 animate-spin ml-1" />
            )}
          </CardTitle>
          <span className="text-[11px] text-slate-500">refreshes every 90s</span>
        </div>
        <CardDescription className="text-slate-600">
          Three platform-level signals in one place. Click any "Open" button to land on the source page with one-click remediation.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {data.signals.map((s) => {
          const Icon = KIND_ICON[s.kind] || ShieldAlert;
          const tone = SEVERITY_TONE[s.severity] || SEVERITY_TONE.warn;
          const isOk = s.severity === 'ok';
          return (
            <div
              key={s.kind}
              data-testid={`platform-health-signal-${s.kind}`}
              data-severity={s.severity}
              className={`flex items-start gap-3 p-3 rounded-lg border bg-white ${tone.border}`}
            >
              <div className={`p-1.5 rounded shrink-0 ${tone.bg}`}>
                {isOk
                  ? <CheckCircle2 className={`h-4 w-4 ${tone.icon}`} />
                  : <Icon className={`h-4 w-4 ${tone.icon}`} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-slate-900">{s.label}</p>
                  {!isOk && (
                    <Badge variant="outline" className={tone.badge}>
                      <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />
                      {s.count}
                    </Badge>
                  )}
                </div>
                <p className={`text-xs font-medium ${isOk ? 'text-emerald-800' : 'text-slate-700'}`}>
                  {s.summary}
                </p>
                {s.detail && (
                  <p className="text-[11px] text-slate-500 mt-0.5 break-words">
                    {s.detail}
                  </p>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                className={`shrink-0 ${tone.button}`}
                onClick={() => navigate(s.action_path)}
                data-testid={`platform-health-open-${s.kind}`}
              >
                Open
                <ChevronRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default PlatformHealthTriageWidget;
